import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Plus, Edit2, Trash2, Loader2, FileText, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_TYPES = [
  { value: 'checklist', label: 'Checklist' },
  { value: 'guide', label: 'Guide' },
  { value: 'template', label: 'Template' },
  { value: 'blueprint', label: 'Blueprint' },
  { value: 'workbook', label: 'Workbook' },
  { value: 'ebook', label: 'eBook' },
  { value: 'video', label: 'Video' },
];

const EMPTY = {
  title: '',
  slug: '',
  short_description: '',
  description: '',
  resource_type: 'checklist',
  download_url: '',
  is_gated: true,
  order: 0,
  is_active: true,
  category: 'free',
};

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or resource object
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/resources`);
      setItems((r.data || []).sort((a, b) => (a.order || 99) - (b.order || 99)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (r) => setEditing({ ...EMPTY, ...r });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing.title?.trim() || !editing.slug?.trim()) {
      toast.error('Title and slug are required');
      return;
    }
    if (!editing.description?.trim() || !editing.short_description?.trim()) {
      toast.error('Both short description and full description are required');
      return;
    }
    setSaving(true);
    // Build payload aligned to backend ResourceCreate model
    const payload = {
      title: editing.title.trim(),
      slug: editing.slug.trim(),
      description: editing.description.trim(),
      short_description: editing.short_description.trim(),
      resource_type: editing.resource_type || 'checklist',
      download_url: editing.download_url || null,
      is_gated: !!editing.is_gated,
      order: parseInt(editing.order) || 0,
      is_active: !!editing.is_active,
      category: editing.category === 'business' ? 'business' : 'free',
    };
    try {
      if (editing.resource_id) {
        await axios.patch(`${API_URL}/api/admin/resources/${editing.resource_id}`, payload, { withCredentials: true });
        toast.success('Resource updated');
      } else {
        await axios.post(`${API_URL}/api/admin/resources`, payload, { withCredentials: true });
        toast.success('Resource created');
      }
      setEditing(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)}: ${d.msg}`).join('; ') : (detail || 'Could not save resource');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this resource? Existing downloads stay logged.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/resources/${id}`, { withCredentials: true });
      toast.success('Resource deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <AdminLayout title="Free Resources">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1" data-testid="admin-resources-title">Free Resources Catalog</h1>
          <p className="text-white/85 text-sm">Manage every downloadable checklist, blueprint, or guide offered on the public site. Visitors download from <code className="bg-white/10 px-1 rounded">/resources</code>.</p>
        </div>

        {!editing && (
          <button onClick={startNew} className="btn-primary-blue inline-flex items-center gap-2" data-testid="resource-new-btn">
            <Plus size={14} /> New Resource
          </button>
        )}

        {editing && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="resource-editor">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#0A0F1C]">{editing.resource_id ? 'Edit Resource' : 'New Resource'}</h2>
              <button onClick={cancelEdit} className="text-[#94A3B8] hover:text-red-600"><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Input label="Title *" v={editing.title} onChange={(v) => setEditing({ ...editing, title: v, slug: editing.slug || slugify(v) })} testid="res-title" />
              <Input label="Slug *" v={editing.slug} onChange={(v) => setEditing({ ...editing, slug: slugify(v) })} testid="res-slug" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">Resource type</span>
                <select
                  value={editing.resource_type || 'checklist'}
                  onChange={(e) => setEditing({ ...editing, resource_type: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C] bg-white"
                  data-testid="res-type"
                >
                  {RESOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">Member portal section</span>
                <select
                  value={editing.category || 'free'}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C] bg-white"
                  data-testid="res-category"
                >
                  <option value="business">Business Resources (members-only, top)</option>
                  <option value="free">Free Resources (bottom)</option>
                </select>
              </label>
            </div>
            <Input label="Display order" type="number" v={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} testid="res-order" />
            <Input label="Short description (one line) *" v={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} testid="res-short" />
            <Textarea label="Full description *" v={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="res-desc" />
            <Input label="Download URL (PDF / file link)" v={editing.download_url} onChange={(v) => setEditing({ ...editing, download_url: v })} testid="res-file" placeholder="https://..." />
            <div className="grid md:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={!!editing.is_gated} onChange={(e) => setEditing({ ...editing, is_gated: e.target.checked })} data-testid="res-gated" />
                <span className="text-[#475569] font-bold">Gated (require email to download)</span>
              </label>
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="res-active" />
                <span className="text-[#475569] font-bold">Active (visible on /resources)</span>
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={save} disabled={saving} className="btn-primary-orange inline-flex items-center gap-2" data-testid="res-save">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save</>}
              </button>
              <button onClick={cancelEdit} className="btn-outline" data-testid="res-cancel">Cancel</button>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0]"><h2 className="font-bold text-[#0A0F1C]">Resources ({items.length})</h2></div>
          {loading ? (
            <div className="text-center py-12 text-[#94A3B8]"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#94A3B8]">No resources yet. Click "New Resource" above.</div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]">
              {items.map((r) => (
                <div key={r.resource_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`res-row-${r.slug}`}>
                  <div className="w-10 h-10 bg-[#FFF7ED] rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-[#F97316]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0A0F1C]">{r.title}</div>
                    <div className="text-xs text-[#475569] truncate">{r.short_description || '—'}</div>
                    <div className="text-[11px] text-[#94A3B8] mt-0.5">
                      <code className="bg-[#F1F5F9] px-1 rounded">/{r.slug}</code> · order #{r.order || 0} · {r.resource_type || 'n/a'}
                      {!r.is_active && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-red-600">Hidden</span>}
                      {r.is_gated && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8]">Gated</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(r)} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`res-edit-${r.slug}`}><Edit2 size={12} /> Edit</button>
                    <button onClick={() => remove(r.resource_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`res-delete-${r.slug}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Input({ label, v, onChange, testid, type = 'text', placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">{label}</span>
      <input type={type} value={v ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C]" data-testid={testid} />
    </label>
  );
}

function Textarea({ label, v, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">{label}</span>
      <textarea value={v ?? ''} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C]" data-testid={testid} />
    </label>
  );
}
